<template>
  <v-container
    v-if="!$apollo.loading"
    fill-height
    fluid
    grid-list-xl
  >
    <v-layout
      v-if="!editMode"
      justify-center
      wrap
    >
      <v-flex
        md8
        xs12
        mb-0
        mt-0
        pb-0
      >
        <h2>{{ $t('modules.promoCodes.title') }}</h2>
      </v-flex>
      <v-flex
        md4
        xs12
        mb-0
        :class="!$vuetify.breakpoint.xs ? 'pt-11 text-right': 'pb-5'"
        pb-0
      >
        <v-btn
          style="display:inline;margin-top:-6px;"
          small
          class="primary"
          @click="editMode = true"
        >
          {{ $t('modules.promoCodes.create') }}
        </v-btn>
      </v-flex>
      <v-flex
        v-if="modulePromoCodes && modulePromoCodes.data.length > 0"
        md12
      >
        <v-card>
          <v-data-table
            class="text-no-wrap"
            :headers="headers"
            :items="modulePromoCodes.data"
            :footer-props="{
              'items-per-page-options': [10, 15, 25, 50, 100]
            }"
            :mobile-breakpoint="900"
            :items-per-page="modulePromoCodes.per_page"
            :page="modulePromoCodes.current_page"
            :server-items-length="modulePromoCodes.total"
            :hide-default-footer="_.isEmpty(modulePromoCodes.data)"
            @update:items-per-page="reload($event, 'count')"
            @update:page="reload($event, 'page')"
          >
            <template v-slot:item.user="{ item }">
              <v-layout
                v-if="item.user"
                wrap
              >
                <v-flex
                  md3
                  xs4
                  mt-4
                  mb-1
                >
                  <v-avatar
                    v-if="item.user.avatar != null"
                    style="cursor: pointer;"
                    :tile="false"
                    :size="30"
                  >
                    <v-img
                      :src="item.user.avatar"
                      :lazy-src="item.user.avatar"
                      max-height="30"
                      contain
                    />
                  </v-avatar>
                  <v-avatar
                    v-else
                    style="cursor: pointer;"
                    :tile="false"
                    :size="30"
                    :color="$vuetify.theme.dark ? $vuetify.theme.themes.dark.background : $vuetify.theme.themes.light.background"
                  >
                    <strong>
                      <a>{{ item.user.name | uppercase | substring(0,2) }}</a>
                    </strong>
                  </v-avatar>
                </v-flex>
                <v-flex
                  md9
                  xs8
                  mt-2
                  mb-2
                >
                  <small><b>{{ item.user.name }}</b><br>
                    {{ item.user.email }}</small>
                </v-flex>
              </v-layout>
            </template>
            <template v-slot:item.status="{ item }">
              <span><b>{{ $t(`modules.promoCodes.statuses.${item.status}`) }}</b></span>
            </template>
            <template v-slot:item.created_at="{ item }">
              <small>{{ item.created_at | moment("L") }}</small>
            </template>
            <template v-slot:item.expires_at="{ item }">
              <small>{{ item.expires_at | moment("L") }}</small>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-menu
                offset-y
                left
                bottom
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    outlined
                    small
                    text
                    v-on="on"
                  >
                    {{ $t('globals.manageButton') }}
                  </v-btn>
                </template>
                <v-sheet dense>
                  <v-list-item
                    @click="editPromoCode(item)"
                  >
                    <v-list-item-title>{{ $t('globals.editButton') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    @click="confirmDeletePromoCode(item)"
                  >
                    <v-list-item-title>{{ $t('globals.deleteButton') }}</v-list-item-title>
                  </v-list-item>
                </v-sheet>
              </v-menu>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
      <v-flex
        v-else
        class="not-found"
      >
        <v-card>
          <v-icon>mdi-ghost</v-icon>
          <p>{{ $t('modules.promoCodes.codeNotFound') }}</p>
        </v-card>
      </v-flex>
    </v-layout>
    <edit-form
      v-if="editMode"
      :promo-code="selectedPromoCodeFormatted"
      :whitelabel-uuid="getWhiteLabel.uuid"
      @closeEditMode="closeEditMode"
    />
    <helper-confirm-dialog
      v-if="confirmDialog"
      :show-dialog="confirmDialog"
      :text="$t('modules.promoCodes.reallyDelete', {name: selectedPromoCode.code})"
      @closeConfirmDialog="closeConfirmDialog"
    />
  </v-container>
</template>

<script>

import EditForm from "@/views/Modules/PromoCodes/NewOrEdit.vue";
import {mapGetters} from 'vuex'
import PROMO_CODES_QUERY from "@/graphql/queries/modules/promoCodes/modulePromoCodes.gql"
import PROMO_CODE_DELETE_MUTATION from "@/graphql/mutations/modules/promoCodes/modulePromoCodesDelete.gql"

export default {
  name: 'ModulePromoCodes',
  components: {
    EditForm
  },
  data() {
    return {
      params: {
        whitelabel_uuid: null,
        count: 10,
        page: 1,
      },
      loading: false,
      editMode: false,
      selectedPromoCode: null,
      confirmDialog: false,
      headers: [
        {
          sortable: false,
          text: this.$t('modules.promoCodes.userLabel'),
          value: 'user'
        },
        {
          sortable: false,
          text: this.$t('modules.promoCodes.promoLabel'),
          value: 'code',
        },
        {
          sortable: false,
          text: this.$t('modules.promoCodes.statusLabel'),
          value: 'status'
        },
        {
          sortable: false,
          text: this.$t('modules.promoCodes.createdAtLabel'),
          value: 'created_at'
        },
        {
          sortable: false,
          text: this.$t('modules.promoCodes.expiresAtLabel'),
          value: 'expires_at'
        },
        {
          sortable: false,
          text: '',
          value: 'actions',
        },
      ]
    }
  },
  computed: {
    ...mapGetters("whitelabel", ["getWhiteLabel"]),
    selectedPromoCodeFormatted() {
      if (this.selectedPromoCode) {
        const {
          uuid,
          user,
          code,
          status,
          product_categories,
          products,
          discount,
          discount_type,
          max_usage_limit,
          issue_after_signup,
          expires_at,
        } = this.selectedPromoCode;
        return {
          uuid,
          user_uuid: user && user.uuid,
          user: user,
          promoCode: code,
          status,
          forProductCategories: JSON.parse(product_categories),
          forProducts: JSON.parse(products),
          discount,
          discount_unit: discount_type,
          issue_after_signup: issue_after_signup || false,
          usageLimit: max_usage_limit,
          expires_at: expires_at && new Date(expires_at).toISOString().split('T')[0]
        }
      }
      return null;
    }
  },
  watch: {
    getWhiteLabel(val) {
      if (val && !val.modules.find(({module}) => module === 'promocodes')) {
        this.$router.push('/dashboard')
      }
      this.params.whitelabel_uuid = val.uuid
      this.$apollo.queries.modulePromoCodes.refetch()
    }
  },
  mounted() {
    if (this.getWhiteLabel) {
      this.params.whitelabel_uuid = this.getWhiteLabel.uuid
    }
  },
  methods: {
    closeConfirmDialog(confirmed) {
      this.confirmDialog = false
      if (confirmed) {
        this.deletePromoCode()
      }
      this.selectedPromoCode = null
    },
    confirmDeletePromoCode(item) {
      this.confirmDialog = true
      this.selectedPromoCode = item
    },
    editPromoCode(item) {
      this.selectedPromoCode = item
      this.editMode = true
    },
    deletePromoCode() {
      this.loading = true;
      let params = {
        whitelabel_uuid: this.getWhiteLabel.uuid,
        promocode_uuid: this.selectedPromoCode.uuid
      }
      this.$apollo
          .mutate({
            mutation: PROMO_CODE_DELETE_MUTATION,
            variables: params
          })
          .then(({data}) => {
            this.$store.commit('app/setNotifier', data.modulePromoCodesDelete.message);
            this.$apollo.queries.modulePromoCodes.refetch()
          })
          .catch(error => {
            this.loading = false;
            if (error.graphQLErrors[0].extensions.category == 'authorization' && error.graphQLErrors[0].message === "This action is unauthorized.") {
              this.$store.commit("app/setShowPermission");
              return;
            }
            this.errorHandler(error);
          });
      this.loading = false;
    },
    closeEditMode(val) {
      this.editMode = false
      if (val) {
        setTimeout(() => {
          this.$apollo.queries.modulePromoCodes.refetch()
        }, 100);
      }
      this.selectedPromoCode = null
    },
    reload(value, from) {
      if (value && from) {
        this.params[from] = value
      }
      this.$apollo.queries.modulePromoCodes.refresh();
    },
  },
  apollo: {
    modulePromoCodes: {
      fetchPolicy: 'network-only',
      query() {
        return PROMO_CODES_QUERY;
      },
      variables() {
        return this.params
      },
      skip() {
        return !this.params.whitelabel_uuid;
      },
    },
  },
}
</script>
